import { create } from 'zustand';
import { 
  PostType, 
  ExtendedPost, 
  PostMetadata, 
  PostSection,
  DeepDivePost,
  BlogPost,
  TwitterPost,
  NewsPost,
  PostRelationship,
  RelationshipType,
  TechnicalLevel,
  ContentTemperature,
  Sentiment
} from '@/lib/types/posts';
import { supabase } from '@/lib/supabase/client';

// Templates for different post types
const postTemplates = {
  [PostType.DEEP_DIVE]: {
    post_type: PostType.DEEP_DIVE,
    title: '',
    excerpt: '',
    sections: [
      { id: 'intro', title: 'Introduction', content: '', display_order: 0 },
      { id: 'analysis', title: 'Analysis', content: '', display_order: 1 },
      { id: 'conclusion', title: 'Conclusion', content: '', display_order: 2 },
      { id: 'references', title: 'References', content: '', display_order: 3 }
    ],
    complexity_score: 3,
    technical_level: TechnicalLevel.INTERMEDIATE,
    content_temperature: ContentTemperature.EVERGREEN,
    key_takeaways: [],
    relationships: [],
    metadata: {
      tags: [],
      use_case: 'educational'
    }
  },
  [PostType.BLOG]: {
    post_type: PostType.BLOG,
    title: '',
    excerpt: '',
    content: '',
    complexity_score: 2,
    technical_level: TechnicalLevel.BEGINNER,
    content_temperature: ContentTemperature.EVERGREEN,
    key_takeaways: [],
    relationships: [],
    metadata: {
      tags: [],
      use_case: 'educational'
    }
  },
  [PostType.TWITTER]: {
    post_type: PostType.TWITTER,
    title: '',
    content: '',
    complexity_score: 1,
    technical_level: TechnicalLevel.BEGINNER,
    content_temperature: ContentTemperature.TIME_SENSITIVE,
    relationships: [],
    metadata: {
      tags: []
    }
  },
  [PostType.NEWS]: {
    post_type: PostType.NEWS,
    title: '',
    excerpt: '',
    content: '',
    complexity_score: 2,
    technical_level: TechnicalLevel.BEGINNER,
    content_temperature: ContentTemperature.TIME_SENSITIVE,
    relationships: [],
    metadata: {
      tags: []
    }
  }
};

interface PostCreationState {
  // Current post being edited
  currentPost: DeepDivePost | BlogPost | TwitterPost | NewsPost;
  
  // Post type selection
  selectedPostType: PostType;
  
  // UI state
  isLoading: boolean;
  activeStep: number;
  error: string | null;
  
  // Actions
  setPostType: (type: PostType) => void;
  updatePostField: <K extends keyof ExtendedPost>(key: K, value: ExtendedPost[K]) => void;
  updateMetadataField: (path: string[], value: any) => void;
  addSection: (section: Omit<PostSection, 'id'>) => void;
  updateSection: (sectionId: string, updates: Partial<PostSection>) => void;
  deleteSection: (sectionId: string) => void;
  moveSection: (sectionId: string, direction: 'up' | 'down') => void;
  savePost: (publish?: boolean) => Promise<number | null>;
  schedulePost: (scheduledFor: string) => Promise<number | null>;
  resetForm: () => void;
  setActiveStep: (step: number) => void;
  
  // Calculated values
  getEstimatedReadingTime: () => number;
}

export const usePostCreationStore = create<PostCreationState>((set, get) => ({
  currentPost: { 
    ...postTemplates[PostType.BLOG],
    id: 0,
    author_id: '',
    is_published: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  } as BlogPost,
  selectedPostType: PostType.BLOG,
  isLoading: false,
  activeStep: 0,
  error: null,
  
  setPostType: (type: PostType) => {
    // Reset current post and use template for selected type
    set({ 
      selectedPostType: type,
      currentPost: { 
        ...postTemplates[type],
        id: 0,
        author_id: '',
        is_published: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as any,
      activeStep: 0
    });
  },
  
  updatePostField: (key, value) => {
    set(state => ({
      currentPost: {
        ...state.currentPost,
        [key]: value
      }
    }));
  },
  
  updateMetadataField: (path, value) => {
    set(state => {
      // Clone current metadata or create empty object
      const metadata = { ...(state.currentPost.metadata || {}) } as Record<string, any>;
      
      // Navigate to the right place in the metadata object
      let target = metadata as Record<string, any>;
      for (let i = 0; i < path.length - 1; i++) {
        if (!target[path[i]]) {
          target[path[i]] = {};
        }
        target = target[path[i]] as Record<string, any>;
      }
      
      // Update the value
      target[path[path.length - 1]] = value;
      
      return {
        currentPost: {
          ...state.currentPost,
          metadata
        }
      };
    });
  },
  
  // Deep Dive specific section management
  addSection: (section) => {
    set(state => {
      if (state.selectedPostType !== PostType.DEEP_DIVE) return state;
      
      const currentPost = state.currentPost as DeepDivePost;
      const sections = [...(currentPost.sections || [])];
      
      return {
        currentPost: {
          ...currentPost,
          sections: [
            ...sections,
            {
              ...section,
              id: `section-${Date.now()}`, // Generate a client-side ID
            }
          ]
        }
      };
    });
  },
  
  updateSection: (sectionId, updates) => {
    set(state => {
      if (state.selectedPostType !== PostType.DEEP_DIVE) return state;
      
      const currentPost = state.currentPost as DeepDivePost;
      const sections = [...(currentPost.sections || [])];
      const sectionIndex = sections.findIndex(s => s.id === sectionId);
      
      if (sectionIndex === -1) return state;
      
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        ...updates
      };
      
      return {
        currentPost: {
          ...currentPost,
          sections
        }
      };
    });
  },
  
  deleteSection: (sectionId) => {
    set(state => {
      if (state.selectedPostType !== PostType.DEEP_DIVE) return state;
      
      const currentPost = state.currentPost as DeepDivePost;
      const sections = [...(currentPost.sections || [])];
      
      return {
        currentPost: {
          ...currentPost,
          sections: sections.filter(s => s.id !== sectionId)
        }
      };
    });
  },
  
  moveSection: (sectionId, direction) => {
    set(state => {
      if (state.selectedPostType !== PostType.DEEP_DIVE) return state;
      
      const currentPost = state.currentPost as DeepDivePost;
      const sections = [...(currentPost.sections || [])];
      const sectionIndex = sections.findIndex(s => s.id === sectionId);
      
      if (sectionIndex === -1) return state;
      
      const newIndex = direction === 'up' 
        ? Math.max(0, sectionIndex - 1)
        : Math.min(sections.length - 1, sectionIndex + 1);
        
      if (newIndex === sectionIndex) return state;
      
      // Swap sections
      const temp = sections[sectionIndex];
      sections[sectionIndex] = sections[newIndex];
      sections[newIndex] = temp;
      
      // Update display order
      sections.forEach((section, idx) => {
        section.display_order = idx;
      });
      
      return {
        currentPost: {
          ...currentPost,
          sections
        }
      };
    });
  },
  
  savePost: async (publish = false) => {
    set({ isLoading: true, error: null });
    
    try {
      const state = get();
      const { currentPost, selectedPostType } = state;
      
      // Prepare post data
      const postData: any = {
        title: currentPost.title,
        content: currentPost.content,
        post_type: selectedPostType,
        is_published: publish,
        excerpt: currentPost.excerpt,
        summary: currentPost.summary,
        reading_time: currentPost.reading_time || state.getEstimatedReadingTime(),
        metadata: currentPost.metadata,
        complexity_score: currentPost.complexity_score,
        technical_level: currentPost.technical_level,
        content_temperature: currentPost.content_temperature,
        target_audience: currentPost.target_audience,
        confidence_score: currentPost.confidence_score,
        key_takeaways: currentPost.key_takeaways,
        sentiment: currentPost.sentiment
      };
      
      // Handle Deep Dive sections
      if (selectedPostType === PostType.DEEP_DIVE) {
        const deepDivePost = currentPost as DeepDivePost;
        if (deepDivePost.sections && deepDivePost.sections.length > 0) {
          // Combine section content for the main content field
          postData.content = deepDivePost.sections
            .sort((a, b) => a.display_order - b.display_order)
            .map(section => `## ${section.title}\n\n${section.content}`)
            .join('\n\n');
            
          // Store sections in metadata for future editing
          if (!postData.metadata) postData.metadata = {};
          postData.metadata.sections = deepDivePost.sections;
        }
      }
      
      // Save post to database
      let postId = currentPost.id;
      let result;
      
      if (postId) {
        // Update existing post
        result = await supabase
          .from('posts')
          .update(postData)
          .eq('id', postId)
          .select()
          .single();
      } else {
        // Create new post
        result = await supabase
          .from('posts')
          .insert(postData)
          .select()
          .single();
      }
      
      if (result.error) throw result.error;
      
      postId = result.data.id;
      
      // Handle relationships if they exist
      if (currentPost.relationships && currentPost.relationships.length > 0) {
        // First, delete any existing relationships for this post
        if (postId) {
          await supabase
            .from('post_relationships')
            .delete()
            .eq('source_post_id', postId);
        }
        
        // Then insert new relationships
        const relationshipData = currentPost.relationships.map(rel => ({
          source_post_id: postId,
          target_post_id: rel.target_post_id,
          relationship_type: rel.relationship_type,
          order_in_series: rel.order_in_series
        }));
        
        const { error: relError } = await supabase
          .from('post_relationships')
          .insert(relationshipData);
          
        if (relError) {
          console.error('Error saving relationships:', relError);
          // Don't throw here, we still want to return the post ID
        }
      }
      
      set({ 
        isLoading: false,
        currentPost: {
          ...currentPost,
          id: postId
        } as any
      });
      
      return postId;
    } catch (error: any) {
      console.error('Error saving post:', error);
      set({ 
        isLoading: false, 
        error: error.message || 'Failed to save post' 
      });
      return null;
    }
  },
  
  schedulePost: async (scheduledFor) => {
    set({ isLoading: true, error: null });
    
    try {
      const state = get();
      const { currentPost } = state;
      
      // First save the post as unpublished
      const postId = await state.savePost(false);
      
      if (!postId) {
        throw new Error('Failed to save post for scheduling');
      }
      
      // Then schedule it
      const { error } = await supabase
        .from('scheduled_posts')
        .upsert({
          post_id: postId,
          scheduled_for: scheduledFor
        });
        
      if (error) throw error;
      
      set({ isLoading: false });
      return postId;
    } catch (error: any) {
      console.error('Error scheduling post:', error);
      set({ 
        isLoading: false, 
        error: error.message || 'Failed to schedule post' 
      });
      return null;
    }
  },
  
  resetForm: () => {
    const { selectedPostType } = get();
    set({ 
      currentPost: { 
        ...postTemplates[selectedPostType],
        id: 0,
        author_id: '',
        is_published: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as any,
      activeStep: 0,
      error: null
    });
  },
  
  setActiveStep: (step) => {
    set({ activeStep: step });
  },
  
  getEstimatedReadingTime: () => {
    const { currentPost, selectedPostType } = get();
    
    let wordCount = 0;
    
    if (selectedPostType === PostType.DEEP_DIVE) {
      const deepDivePost = currentPost as DeepDivePost;
      if (deepDivePost.sections) {
        wordCount = deepDivePost.sections.reduce((count, section) => {
          return count + (section.content.split(/\s+/).length || 0);
        }, 0);
      }
    } else {
      wordCount = (currentPost.content?.split(/\s+/).length || 0);
    }
    
    // Average reading speed: 200-250 words per minute
    // We'll use 225 words per minute
    const minutes = Math.ceil(wordCount / 225);
    return Math.max(1, minutes); // Minimum 1 minute
  }
})); 